// src/pages/Home.tsx
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Car, Settings, ArrowRight, ChevronRight } from 'lucide-react';

const CAROUSEL_INTERVAL = 5000; // 5 seconds

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);

  const carouselImages = useMemo(() => [
    "/assets/images/domov/page-hero1.jpg",
    "/assets/images/domov/page-hero2.jpg"
  ], []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, CAROUSEL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [carouselImages]);

  return (
    <div>
      {/* Hero Section with Carousel */}
      <section className="relative bg-gray-900 text-white hero-section h-[90vh]">
        <div className="absolute inset-0 overflow-hidden">
          {carouselImages.map((image, index) => (
            <div 
              key={index}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${index === activeSlide ? 'opacity-100' : 'opacity-0'}`}
              style={{ backgroundImage: `url('${image}')` }}
            ></div>
          ))}
        </div>
        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10 text-center">
          <div className="max-w-2xl hero-content mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold fs mb-6 text-white">Vaš zanesljiv avtoservis za vse znamke vozil</h1>
            <p className="text-xl mb-8 text-gray-100">Popravila in servis vseh znamk vozil, priprava na tehnični pregled, polnjenje klimatskih naprav in diagnostika vozil.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary inline-flex items-center justify-center group">
                Kontaktirajte nas
                <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
              </Link>
              <Link to="/avtoservis" className="bg-white text-autoDark font-medium py-3 px-6 rounded-full hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-1 group">
                Naše storitve
                <ChevronRight className="ml-1 group-hover:ml-2 transition-all duration-300" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-autoGray">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">Naše storitve</h2>
          <div className="services-grid">
            {/* Service 1 */}
            <div className="service-card group">
              <div className="service-icon group-hover:text-autoTeal">
                <Car />
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-autoBlue transition-colors duration-300">Avtoservis vseh znamk</h3>
              <p className="text-gray-600 mb-4">Popravljamo in servisiramo vozila vseh znamk. Naši strokovnjaki poskrbijo za vaše vozilo.</p>
              <Link to="/avtoservis" className="text-autoBlue font-medium inline-flex items-center hover:underline group">
                Več o storitvi
                <ArrowRight className="ml-1 group-hover:ml-2 transition-all duration-300" size={16} />
              </Link>
            </div>

            {/* Service 2 */}
            <div className="service-card group">
              <div className="service-icon group-hover:text-autoOrange">
                <Wrench />
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-autoOrange transition-colors duration-300">Obnova turbo polnilnikov</h3>
              <p className="text-gray-600 mb-4">Specializirani smo za obnovo turbo polnilnikov. Hitra in zanesljiva storitev z garancijo.</p>
              <Link to="/obnova-turbo" className="text-autoBlue font-medium inline-flex items-center hover:underline group">
                Več o storitvi
                <ArrowRight className="ml-1 group-hover:ml-2 transition-all duration-300" size={16} />
              </Link>
            </div>

            {/* Service 3 */}
            <div className="service-card group">
              <div className="service-icon group-hover:text-autoPurple">
                <Settings />
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-autoPurple transition-colors duration-300">Diagnostika vozil</h3>
              <p className="text-gray-600 mb-4">Najsodobnejša oprema za diagnostiko vseh znamk vozil. Hitro in natančno odkrivanje napak.</p>
              <Link to="/avtoservis" className="text-autoBlue font-medium inline-flex items-center hover:underline group">
                Več o storitvi
                <ArrowRight className="ml-1 group-hover:ml-2 transition-all duration-300" size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12 after:left-1/2 after:-translate-x-1/2">O nas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-md transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow">
              <img 
                src="/assets/images/o-nas/avtoservis-delavnica.jpg" 
                alt="Avtoservis delavnica" 
                className="rounded-xl shadow-md w-full h-auto"
              />
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,122,217,0.6)] hover:border-autoBlue border border-gray-100 card-glow flex flex-col justify-between">
              <div>
                <p className="mb-4">Smo družinsko podjetje s tradicijo od leta 1996. Kot eni prvih v Sloveniji smo se pridružili servisni mreži Bosch car servis, pod okriljem katerih smo tehnično opremljeni z najsodobnejšo diagnozo.</p>
                <p className="mb-6">Naša ekipa izkušenih strokovnjakov poskrbijo za vaše vozilo. Popravila in vzdrževanje opravljamo strokovno in hitro.</p>
              </div>
              <div className="flex justify-center">
                <Link to="/o-nas" className="btn-primary inline-flex items-center group">
                  Spoznajte nas
                  <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-autoBlue to-autoPurple text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in">Potrebujete pomoč z vašim vozilom?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Pokličite nas ali izpolnite kontaktni obrazec. Odgovorili vam bomo v najkrajšem možnem času.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:041273738" className="bg-white text-autoBlue font-medium py-3 px-6 rounded-full hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Pokličite nas: 041 273 738
            </a>
            <Link to="/kontakt" className="border-2 border-white text-white font-medium py-3 px-6 rounded-full hover:bg-white hover:text-autoBlue transition-all duration-300 inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 group">
              Pošljite povpraševanje
              <ArrowRight className="ml-2 group-hover:ml-3 transition-all duration-300" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;